// FILE: src/pages/MarketingOverview.jsx
import React from "react";
import Card from "../components/ui/Card";
import ChartCard from "../components/ui/ChartCard";
import DataTable from "../components/ui/DataTable";

const MarketingOverview = () => {
  // Marketing KPIs
  const marketingKPIs = [
    {
      title: "Total Visitors",
      value: "84,532",
      change: "+12.5%",
      changeType: "increase",
      icon: "👥",
      description: "from last month",
    },
    {
      title: "Conversion Rate",
      value: "4.23%",
      change: "+0.8%",
      changeType: "increase",
      icon: "🎯",
      description: "from last month",
    },
    {
      title: "Cost Per Click",
      value: "$2.45",
      change: "-5.2%",
      changeType: "decrease",
      icon: "💰",
      description: "from last month",
    },
    {
      title: "ROI",
      value: "340%",
      change: "+15.7%",
      changeType: "increase",
      icon: "📊",
      description: "from last month",
    },
  ];

  // Traffic Source Data
  const trafficSources = [
    {
      source: "Organic Search",
      visitors: 28934,
      percentage: "34.2%",
      color: "bg-blue-500",
    },
    {
      source: "Direct",
      visitors: 21567,
      percentage: "25.5%",
      color: "bg-green-500",
    },
    {
      source: "Social Media",
      visitors: 15432,
      percentage: "18.3%",
      color: "bg-purple-500",
    },
    {
      source: "Email Marketing",
      visitors: 9876,
      percentage: "11.7%",
      color: "bg-yellow-500",
    },
    {
      source: "Paid Ads",
      visitors: 6543,
      percentage: "7.7%",
      color: "bg-red-500",
    },
    {
      source: "Referral",
      visitors: 2180,
      percentage: "2.6%",
      color: "bg-gray-500",
    },
  ];

  // Campaign Performance Data
  const campaignData = [
    {
      id: 1,
      name: "Summer Sale 2024",
      type: "PPC",
      status: "Active",
      budget: "$5,000",
      spent: "$3,245",
      clicks: 12543,
      conversions: 234,
      ctr: "3.45%",
      cpc: "$0.89",
    },
    {
      id: 2,
      name: "Brand Awareness Q2",
      type: "Display",
      status: "Active",
      budget: "$8,000",
      spent: "$6,789",
      clicks: 23456,
      conversions: 156,
      ctr: "2.87%",
      cpc: "$1.23",
    },
    {
      id: 3,
      name: "Product Launch",
      type: "Social",
      status: "Paused",
      budget: "$3,500",
      spent: "$3,500",
      clicks: 8934,
      conversions: 89,
      ctr: "4.12%",
      cpc: "$2.10",
    },
    {
      id: 4,
      name: "Retargeting Campaign",
      type: "PPC",
      status: "Active",
      budget: "$2,000",
      spent: "$1,234",
      clicks: 5678,
      conversions: 167,
      ctr: "5.23%",
      cpc: "$0.67",
    },
  ];

  // SEO/PPC Status Cards
  const seoData = {
    organicTraffic: "+23%",
    keywords: "1,247",
    backlinks: "8,934",
    domainAuthority: "67",
  };

  const ppcData = {
    adSpend: "$12,345",
    impressions: "234,567",
    clicks: "8,934",
    qualityScore: "8.5",
  };

  const campaignColumns = [
    { key: "name", label: "Campaign Name" },
    {
      key: "type",
      label: "Type",
      render: (value) => (
        <span
          className={`px-2 py-1 text-xs font-medium rounded-full ${
            value === "PPC"
              ? "bg-blue-100 text-blue-800 dark:bg-blue-900 dark:text-blue-300"
              : value === "Display"
              ? "bg-purple-100 text-purple-800 dark:bg-purple-900 dark:text-purple-300"
              : "bg-green-100 text-green-800 dark:bg-green-900 dark:text-green-300"
          }`}
        >
          {value}
        </span>
      ),
    },
    {
      key: "status",
      label: "Status",
      render: (value) => (
        <span
          className={`px-2 py-1 text-xs font-medium rounded-full ${
            value === "Active"
              ? "bg-green-100 text-green-800 dark:bg-green-900 dark:text-green-300"
              : "bg-gray-100 text-gray-800 dark:bg-gray-900 dark:text-gray-300"
          }`}
        >
          {value}
        </span>
      ),
    },
    { key: "budget", label: "Budget" },
    { key: "spent", label: "Spent" },
    { key: "clicks", label: "Clicks" },
    { key: "conversions", label: "Conversions" },
    { key: "ctr", label: "CTR" },
    { key: "cpc", label: "CPC" },
  ];

  return (
    <div className="space-y-6">
      {/* Page Header */}
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-3xl font-bold text-gray-900 dark:text-white">
            Marketing Overview
          </h1>
          <p className="text-gray-600 dark:text-gray-400 mt-2">
            Track your marketing performance and campaigns
          </p>
        </div>
        <div className="flex space-x-3">
          <button className="px-4 py-2 bg-white dark:bg-gray-800 border border-gray-300 dark:border-gray-600 rounded-lg text-gray-700 dark:text-gray-300 hover:bg-gray-50 dark:hover:bg-gray-700">
            Create Campaign
          </button>
          <button className="px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700">
            View Analytics
          </button>
        </div>
      </div>

      {/* Marketing KPIs */}
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6">
        {marketingKPIs.map((kpi, index) => (
          <Card key={index} className="hover:shadow-md transition-shadow">
            <div className="flex items-center">
              <div className="flex-shrink-0">
                <div className="w-12 h-12 bg-purple-100 dark:bg-purple-900 rounded-lg flex items-center justify-center">
                  <span className="text-2xl">{kpi.icon}</span>
                </div>
              </div>
              <div className="ml-4 flex-1">
                <p className="text-sm font-medium text-gray-600 dark:text-gray-400">
                  {kpi.title}
                </p>
                <p className="text-2xl font-bold text-gray-900 dark:text-white">
                  {kpi.value}
                </p>
                <div className="flex items-center mt-1">
                  <span
                    className={`text-sm font-medium ${
                      kpi.changeType === "increase"
                        ? "text-green-600"
                        : "text-red-600"
                    }`}
                  >
                    {kpi.change}
                  </span>
                  <span className="text-sm text-gray-500 dark:text-gray-400 ml-1">
                    {kpi.description}
                  </span>
                </div>
              </div>
            </div>
          </Card>
        ))}
      </div>

      {/* Charts and Traffic Sources */}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        {/* Website Traffic Trend */}
        <ChartCard
          title="Website Traffic Trend"
          subtitle="Monthly visitor growth"
          chartType="line"
          data={[
            { name: "Jan", visitors: 67000, pageViews: 134000 },
            { name: "Feb", visitors: 72000, pageViews: 145000 },
            { name: "Mar", visitors: 69000, pageViews: 139000 },
            { name: "Apr", visitors: 78000, pageViews: 158000 },
            { name: "May", visitors: 81000, pageViews: 164000 },
            { name: "Jun", visitors: 84000, pageViews: 171000 },
            { name: "Jul", visitors: 87000, pageViews: 178000 },
          ]}
          dataKey={["visitors", "pageViews"]}
          xAxisKey="name"
          colors={["#3B82F6", "#8B5CF6"]}
          className="lg:col-span-2"
        />

        {/* Traffic Source Donut Chart */}
        <ChartCard
          title="Traffic Sources"
          subtitle="Visitor acquisition channels"
          chartType="donut"
          data={trafficSources.map((source) => ({
            name: source.source,
            value: source.visitors,
          }))}
          dataKey="value"
          colors={[
            "#3B82F6",
            "#10B981",
            "#8B5CF6",
            "#F59E0B",
            "#EF4444",
            "#6B7280",
          ]}
        >
          <div className="space-y-4 mt-4">
            {trafficSources.map((source, index) => (
              <div key={index} className="flex items-center justify-between">
                <div className="flex items-center">
                  <div
                    className={`w-3 h-3 rounded-full ${source.color} mr-3`}
                  ></div>
                  <span className="text-sm font-medium text-gray-700 dark:text-gray-300">
                    {source.source}
                  </span>
                </div>
                <div className="text-right">
                  <div className="text-sm font-bold text-gray-900 dark:text-white">
                    {source.visitors.toLocaleString()}
                  </div>
                  <div className="text-xs text-gray-500 dark:text-gray-400">
                    {source.percentage}
                  </div>
                </div>
              </div>
            ))}
          </div>
        </ChartCard>

        {/* Conversion Funnel Chart */}
        <ChartCard
          title="Conversion Funnel"
          subtitle="User journey analysis"
          chartType="bar"
          data={[
            { name: "Visitors", value: 84532 },
            { name: "Leads", value: 12679 },
            { name: "Qualified", value: 5871 },
            { name: "Opportunities", value: 2346 },
            { name: "Customers", value: 1456 },
          ]}
          dataKey="value"
          xAxisKey="name"
          colors={["#8B5CF6"]}
        />
      </div>

      {/* SEO & PPC Status Cards */}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        <Card title="SEO Performance" subtitle="Organic search metrics">
          <div className="grid grid-cols-2 gap-4">
            <div className="text-center">
              <div className="text-2xl font-bold text-green-600">
                {seoData.organicTraffic}
              </div>
              <div className="text-sm text-gray-500 dark:text-gray-400">
                Organic Traffic
              </div>
            </div>
            <div className="text-center">
              <div className="text-2xl font-bold text-gray-900 dark:text-white">
                {seoData.keywords}
              </div>
              <div className="text-sm text-gray-500 dark:text-gray-400">
                Keywords
              </div>
            </div>
            <div className="text-center">
              <div className="text-2xl font-bold text-gray-900 dark:text-white">
                {seoData.backlinks}
              </div>
              <div className="text-sm text-gray-500 dark:text-gray-400">
                Backlinks
              </div>
            </div>
            <div className="text-center">
              <div className="text-2xl font-bold text-blue-600">
                {seoData.domainAuthority}
              </div>
              <div className="text-sm text-gray-500 dark:text-gray-400">
                Domain Authority
              </div>
            </div>
          </div>
        </Card>

        <Card title="PPC Performance" subtitle="Paid advertising metrics">
          <div className="grid grid-cols-2 gap-4">
            <div className="text-center">
              <div className="text-2xl font-bold text-red-600">
                {ppcData.adSpend}
              </div>
              <div className="text-sm text-gray-500 dark:text-gray-400">
                Ad Spend
              </div>
            </div>
            <div className="text-center">
              <div className="text-2xl font-bold text-gray-900 dark:text-white">
                {ppcData.impressions}
              </div>
              <div className="text-sm text-gray-500 dark:text-gray-400">
                Impressions
              </div>
            </div>
            <div className="text-center">
              <div className="text-2xl font-bold text-gray-900 dark:text-white">
                {ppcData.clicks}
              </div>
              <div className="text-sm text-gray-500 dark:text-gray-400">
                Clicks
              </div>
            </div>
            <div className="text-center">
              <div className="text-2xl font-bold text-green-600">
                {ppcData.qualityScore}
              </div>
              <div className="text-sm text-gray-500 dark:text-gray-400">
                Quality Score
              </div>
            </div>
          </div>
        </Card>
      </div>

      {/* Campaign Performance Table */}
      <Card
        title="Campaign Performance"
        subtitle="Detailed campaign metrics and performance"
      >
        <DataTable
          columns={campaignColumns}
          data={campaignData}
          searchable={true}
          pagination={false}
        />
      </Card>
    </div>
  );
};

export default MarketingOverview;
